// University of Illinois Chicago: public college page (#2652, round 2),
// official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UIC's Common Data Set 2025-2026 (Office of Institutional Research; the
//    OIR Common Data Set page links the PDF CDS_Final_2025_26.pdf): C1
//    applicants, admits and enrollees for Fall 2025, C8 test policy for Fall
//    2027, C21/C22 plans, G1/G4/G5 2026-2027 costs. The C1 residency table
//    gives totals only, so there are no residency rows. C21 marks early
//    decision N/A; C22 lists early action (closing 11/1, notification 12/1)
//    with no counts.
//  - UIC Undergraduate Admissions: Undergraduate Application Deadlines (Fall
//    2027), First Year Requirements, Test Optional FAQ, and Application
//    Process (decision timing; the decision column and the paragraph below
//    the table cite it).
//  - College Scorecard API, school id 145600 (net price by family income).
// Departures: the CDS lists November 1 as the early action closing date; the
// admissions deadlines page gives November 2, 2026 for Fall 2027, and the
// admissions page wins. Cost: the CDS G1/G5 2026-2027 items are used (the
// brief's rule). The financial aid office's 2026-2027 Cost of Attendance
// page gives in-state tuition only, with mandatory fees of $5,068 (CDS:
// $3,294), on-campus housing and food of $17,800 (CDS: $17,100) and
// out-of-state transportation of $3,626; it is not used.
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UIC Common Data Set 2025-2026',
        url: 'https://oir.uic.edu/common-data-set-3/',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'UIC Admissions, Undergraduate Application Deadlines',
        url: 'https://admissions.uic.edu/undergraduate/requirements-deadlines/deadlines',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'UIC Admissions, First Year Requirements',
        url: 'https://admissions.uic.edu/undergraduate/requirements-deadlines/first-year-requirements',
        read: '2026-10-01',
    },
    testFaq: {
        label: 'UIC Admissions, Test Optional FAQ',
        url: 'https://admissions.uic.edu/undergraduate/requirements-deadlines/first-year-requirements/test-optional-faq',
        read: '2026-10-01',
    },
    process: {
        label: 'UIC Admissions, Application Process',
        url: 'https://admissions.uic.edu/undergraduate/application-process',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?145600-University-of-Illinois-Chicago',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-illinois-chicago',
    kbId: 'university_of_illinois_chicago',
    name: 'University of Illinois Chicago',
    shortName: 'UIC',
    aliases: ['UIC', 'Illinois Chicago'],
    location: 'Chicago, IL',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UIC brand guidelines); decoration only.
    branding: {
        primary: '#D50032',
        secondary: '#001E62',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_illinois_chicago_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 29929,
        admitted: 22674,
        enrolled: 5405,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UIC\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major. Each UIC college sets its own admission criteria, and the First Year Requirements page shows the middle 50 percent of grades and scores of admitted students by college, but no acceptance rates.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UIC has no early decision plan. The Common Data Set lists a nonbinding early action plan but gives no early action counts, so it gives no early round acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Each UIC college sets its own admission criteria, and your choice of major is part of the review. Some programs ask for more:',
        rows: [
            { school: 'Performance programs (Theatre, Music)', requirement: 'An audition is required for all performance degree applicants.' },
            { school: 'Honors College and Guaranteed Professional Program Admissions (GPPA)', requirement: 'You must be admitted to UIC before these are reviewed. Each needs its own essay, sent with the application, and two recommendations.' },
            { school: 'Honors College and GPPA deadlines', requirement: 'GPPA: November 2, 2026. Honors College: November 2, 2026 (priority) and February 1, 2027 (final).', source: 'deadlines' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1, 2027' },
        rows: [
            { plan: 'Early Action', deadline: 'November 2, 2026', decision: 'By December 16', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1, 2027', decision: 'Rolling, January through March', binding: false },
        ],
        note: 'Applications open August 1, 2026. For Early Action, the application and every required document must be dated or postmarked by November 2. November 2, 2026 is also the scholarship priority deadline for first-year applicants.',
        source: 'deadlines',
        binding: 'Decision timing: Early Action decisions are released by December 16, and Regular Decision is rolling, January through March. UIC says that historically over 95% of applicants get a decision within these time frames.',
        bindingSource: 'process',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UIC is test-optional: the ACT or SAT is not required, and applicants without scores get full consideration, for merit scholarships too. Scores you send are considered only when they strengthen your application. UIC does not use scores typed into the Common Application or superscore reports. To send scores, upload a PDF of the score report through the Common Application, send an official report, include them on your high school transcript, or have a counselor email the report.',
        source: 'testFaq',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for admission but considered if submitted, for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    currentChanges: [
        { topic: 'Testing', line: 'UIC\'s Test Optional FAQ says its current policy, under which scores count only when they help an application, takes effect beginning with the 2027 undergraduate admission cycle.', source: 'testFaq' },
    ],

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [11652, 28352] },
            { item: 'Required fees', amounts: [3294, 3294] },
            { item: 'Housing and food (on campus)', amounts: [17100, 17100] },
            { item: 'Books and supplies', amounts: [1400, 1400] },
            { item: 'Transportation', amounts: [1526, 1526] },
            { item: 'Other expenses', amounts: [4500, 4500] },
        ],
        totals: [39472, 56172],
        note: 'For a full-time undergraduate living on campus, fall and spring. Tuition varies by program: the Common Data Set says 67.7% of full-time undergraduates pay more than the tuition shown here.',
        source: 'cds',
        where: 'sections G1, G4 and G5',
        netPrice: {
            average: 9540,
            byIncome: [
                { income: '$0 to $30,000', amount: 9536 },
                { income: '$30,001 to $48,000', amount: 7327 },
                { income: '$48,001 to $75,000', amount: 8493 },
                { income: '$75,001 to $110,000', amount: 12812 },
                { income: 'Over $110,000', amount: 24150 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UIC\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
